import { Button, IconButton, ListItem, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Database, FileText, Trash2, Upload } from "lucide-react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteSpaceFileMutation,
  errorMessage,
  listDataSourcesOptions,
  listSpaceKnowledgeOptions,
  listSpaceKnowledgeQueryKey,
  uploadSpaceFilesMutation,
} from "@/api";
import { Page } from "@/components/Page";
import { QuerySection } from "@/components/section";
import { Text } from "@/components/Text";
import { formatRelativeTime } from "@/i18n/format";
import { SpaceItemsList } from "./SpaceItemsList";
import { useSettingsSpace } from "./useSpaceSettings";

function SpaceFiles({ spaceUuid }: Readonly<{ spaceUuid: string }>) {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.knowledge" });
  const toast = useToast();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const path = { space_uuid: spaceUuid };
  const files = useQuery({ ...listSpaceKnowledgeOptions({ path }), select: (r) => r.data });
  const refresh = () => queryClient.invalidateQueries({ queryKey: listSpaceKnowledgeQueryKey({ path }) });
  const upload = useMutation({
    ...uploadSpaceFilesMutation(),
    onSuccess: () => toast.success(t("uploaded")),
    onError: (error) => toast.error(errorMessage(error, t("uploadFailed"))),
    onSettled: refresh,
  });
  const remove = useMutation({
    ...deleteSpaceFileMutation(),
    onSuccess: () => toast.success(t("fileDeleted")),
    onError: (error) => toast.error(errorMessage(error, t("uploadFailed"))),
    onSettled: refresh,
  });

  return (
    <section aria-label={t("files")} className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <Text as="h2" variant="h3">
            {t("files")}
          </Text>
          <Text variant="caption" tone="tertiary">
            {t("filesHint")}
          </Text>
        </div>
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<Upload size={14} />}
          loading={upload.isPending}
          onClick={() => input.current?.click()}
        >
          {upload.isPending ? t("uploading") : t("upload")}
        </Button>
        <input
          ref={input}
          type="file"
          multiple
          hidden
          aria-label={t("upload")}
          onChange={(event) => {
            const chosen = Array.from(event.target.files ?? []);
            event.target.value = "";
            if (chosen.length > 0) upload.mutate({ path, body: { files: chosen } });
          }}
        />
      </div>
      <QuerySection
        query={files}
        isEmpty={(list) => list.length === 0}
        empty={{ icon: <FileText size={18} />, hint: t("filesEmpty") }}
      >
        {(list) => (
          <ul>
            {list.map((file, index) => (
              <li key={file.uuid}>
                <ListItem
                  divider={index > 0}
                  leading={<FileText size={15} className="text-content-tertiary" />}
                  description={formatRelativeTime(file.createdAt)}
                  trailing={
                    <IconButton
                      icon={<Trash2 />}
                      aria-label={t("deleteFile", { name: file.filename })}
                      variant="ghost"
                      size="sm"
                      onClick={() => remove.mutate({ path: { ...path, file_uuid: file.uuid } })}
                    />
                  }
                >
                  {file.filename}
                </ListItem>
              </li>
            ))}
          </ul>
        )}
      </QuerySection>
    </section>
  );
}

export function KnowledgeSection() {
  const { t } = useTranslation("spaces", { keyPrefix: "settings.knowledge" });
  const space = useSettingsSpace();
  const sources = useQuery({
    ...listDataSourcesOptions({ query: { page_size: 100 } }),
    select: (r) => r.data.map((d) => ({ uuid: d.uuid, name: d.name, description: d.description })),
  });
  return (
    <Page title={t("title")}>
      <SpaceFiles spaceUuid={space.uuid} />
      <SpaceItemsList
        spaceUuid={space.uuid}
        type="data_collection"
        title={t("sources")}
        addLabel={t("addSource")}
        addTitle={t("addSourceTitle")}
        emptyHint={t("sourcesEmpty")}
        icon={<Database size={18} />}
        openPath={(uuid) => `/build/data-sources/${uuid}`}
        options={sources}
      />
    </Page>
  );
}
